import { useEffect, useState } from 'react';
import { app } from '../app.ts';
import { useApp } from '../hooks.ts';
import { Icons } from './icons.tsx';
import { COMMANDS, command, formatKeys } from '../commands.ts';
import { startPresentation } from './Present.tsx';
import { AccountMenu, AgentTokens } from './Account.tsx';
import { StartVoteButton } from './Voting.tsx';
import { resolvedTheme } from '../theme.ts';
import { createBranch } from './Branches.tsx';

export function TopBar() {
  const name = useApp('doc', () => app.doc.get(app.doc.rootId)?.name ?? 'Untitled');
  const status = useApp('ui', () => app.ui.status);
  const zoom = useApp('camera', () => app.camera.zoom);
  const peers = useApp('peers', () => [...app.peers.values()]);
  const followId = useApp('ui', () => app.ui.followId);
  const readOnly = useApp('doc', () => app.readOnly);
  const [zoomMenu, setZoomMenu] = useState(false);
  return (
    <div className="topbar">
      <button className="logo" title="All files" onClick={() => app.setUI({ homeOpen: true })}>
        {Icons.logo}
      </button>
      <input
        className="filename"
        key={name}
        defaultValue={name}
        size={Math.max(8, name.length)}
        // clicking in selects the whole name, so typing replaces it (Escape puts it back)
        onFocus={(e) => e.target.select()}
        onBlur={(e) => {
          const v = e.target.value.trim();
          if (v && v !== name) app.editor.update(app.doc.rootId, { name: v }, 'Rename file');
          else e.target.value = name;
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
          const input = e.target as HTMLInputElement;
          if (e.key === 'Enter') input.blur();
          if (e.key === 'Escape') {
            input.value = name;
            input.blur();
          }
        }}
      />
      <FileMenu />
      <button className="icon-btn mobile-only" title="Layers" onClick={() => app.setUI({ showLayers: !app.ui.showLayers, showProps: false })}>
        ☰
      </button>
      <button className="icon-btn mobile-only" title="Properties" onClick={() => app.setUI({ showProps: !app.ui.showProps, showLayers: false })}>
        ⚙
      </button>
      <span className="status" title={status}>
        <span className={`dot ${status}`} />
        {status === 'online' ? 'Saved' : status === 'connecting' ? 'Connecting…' : 'Offline — changes will sync'}
      </span>
      <div className="spacer" />
      <div className="avatars">
        {peers.map((p) => (
          <span
            key={p.clientId}
            className={`avatar ${p.kind === 'agent' ? 'agent' : ''} ${followId === p.clientId ? 'following' : ''}`}
            style={{ background: p.color, cursor: 'pointer', outlineColor: p.color }}
            title={`${p.name}${p.status ? ` — ${p.status}` : ''} · click to ${followId === p.clientId ? 'stop following' : 'follow'}`}
            onClick={() => app.startFollowing(followId === p.clientId ? null : p.clientId)}
          >
            {p.kind === 'agent' ? Icons.sparkle : initials(p.name)}
            {p.status && <span className="busy" />}
            {p.audio && <span className="in-call" title={p.audio.muted ? 'In the call, muted' : 'In the call'}>{p.audio.muted ? '🔇' : '🔊'}</span>}
          </span>
        ))}
        {app.account.authEnabled ? (
          <AccountMenu />
        ) : (
          <span className="avatar" style={{ background: '#1e1e1e' }} title={`${app.userName} (you)`}>
            {initials(app.userName)}
          </span>
        )}
      </div>
      {readOnly && <span className="view-only" title="You can look, comment and present">View only</span>}
      {app.account.authEnabled && (
        <button className="btn secondary" title="Invite people and set link access" onClick={() => app.setUI({ peopleOpen: true })}>
          Share
        </button>
      )}
      <AudioButton />
      <Reactions />
      <SpotlightButton />
      <DevToggle />
      <ActivityButton />
      <StartVoteButton />
      <button className="btn secondary" title="Present prototype (⌥↵)" onClick={startPresentation}>
        ▶ Present
      </button>
      {!app.readOnly && (
        <button className="btn secondary" title="Ask AI (⌘J)" onClick={() => app.setUI({ assistantOpen: !app.ui.assistantOpen })}>
          {Icons.sparkle} Ask AI
        </button>
      )}
      <button className="btn primary" onClick={() => app.setUI({ shareOpen: true })}>
        {Icons.sparkle} Connect AI
      </button>
      <div style={{ position: 'relative' }}>
        <button className="zoom" onClick={() => setZoomMenu(!zoomMenu)}>
          {Math.round(zoom * 100)}% ▾
        </button>
        {zoomMenu && (
          <div className="menu" style={{ position: 'absolute', right: 0, top: 34 }} onMouseLeave={() => setZoomMenu(false)}>
            {['zoomIn', 'zoomOut', 'zoomFit', 'zoomSelection', 'zoom100', '-', 'toggleRulers', 'toggleGrids', 'outlineMode', 'darkMode'].map((id, i) => {
              if (id === '-') return <hr key={i} />;
              const c = command(id);
              const on = id === 'toggleRulers' ? app.ui.showRulers : id === 'toggleGrids' ? app.ui.showGrids : id === 'outlineMode' ? app.ui.outlineMode : id === 'darkMode' ? resolvedTheme() === 'dark' : null;
              return (
                <button key={id} disabled={c.enabled && !c.enabled()} onClick={() => (c.run(), setZoomMenu(false))}>
                  <span>
                    {on !== null && <span style={{ display: 'inline-block', width: 14 }}>{on ? '✓' : ''}</span>}
                    {c.label}
                  </span>
                  <span className="keys">{formatKeys(c.keys)}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function ActivityButton() {
  const [open, setOpen] = useState(false);
  const items = useApp('peers', () => [...app.activity]);
  return (
    <div style={{ position: 'relative' }}>
      <button className="icon-btn" title="Activity" onClick={() => setOpen(!open)}>
        ⏱
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 49 }} onPointerDown={() => setOpen(false)} />
          <div className="menu activity" style={{ position: 'absolute', right: 0, top: 34, width: 300 }}>
            {items.length === 0 && <div style={{ padding: '8px 16px', color: '#999' }}>No changes from collaborators yet.</div>}
            {items.map((a, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, padding: '6px 14px', alignItems: 'center' }}>
                <span style={{ width: 8, height: 8, borderRadius: 4, background: a.color, flex: 'none' }} />
                <span style={{ flex: 1 }}>
                  <b>{a.who}</b> {a.label.toLowerCase()}
                  {a.count > 1 ? ` ×${a.count}` : ''}
                </span>
                <span style={{ color: '#999', fontSize: 10 }}>{timeAgo(a.at)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const EMOJI = ['👍', '❤️', '😂', '🎉', '🔥', '👏', '🤔', '👀'];

/** FigJam-style reactions: a burst everyone sees at your cursor. */
function Reactions() {
  const kind = useApp('doc', () => app.pageKind());
  const [open, setOpen] = useState(false);
  if (kind !== 'WHITEBOARD') return null;
  return (
    <div style={{ position: 'relative' }}>
      <button className="icon-btn" title="React" onClick={() => setOpen(!open)}>
        😊
      </button>
      {open && (
        <div className="reaction-bar" onMouseLeave={() => setOpen(false)}>
          {EMOJI.map((e) => (
            <button key={e} onClick={() => app.react(e)}>
              {e}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Audio call with everyone in the file (peer to peer). */
function AudioButton() {
  useApp('peers', () => `${app.audio.joined}${app.audio.muted}${[...app.peers.values()].filter((p) => p.audio).length}`);
  const others = [...app.peers.values()].filter((p) => p.audio && p.kind === 'human');
  if (!app.audio.joined)
    return (
      <button className="icon-btn" title={others.length ? `Join the audio call (${others.length} in it)` : 'Start an audio call'} onClick={() => void app.audio.join()}>
        🎧{others.length > 0 && <span className="call-count">{others.length}</span>}
      </button>
    );
  return (
    <div className="audio-bar">
      <button className="icon-btn" title={app.audio.muted ? 'Unmute' : 'Mute'} onClick={() => app.audio.toggleMute()}>
        {app.audio.muted ? '🔇' : '🎙'}
      </button>
      <span>{others.length + 1} in call</span>
      <button className="btn secondary" onClick={() => app.audio.leave()}>
        Leave
      </button>
    </div>
  );
}

/** Spotlight: everyone else follows your view until you stop. */
function SpotlightButton() {
  const on = useApp('ui', () => app.ui.spotlighting);
  const others = useApp('peers', () => [...app.peers.values()].some((p) => p.kind === 'human'));
  if (!others && !on) return null;
  return (
    <button className={`btn ${on ? 'dev-on' : 'secondary'}`} title="Bring everyone to your view" onClick={() => app.setSpotlight(!on)}>
      {on ? '◉ Spotlighting' : '◎ Spotlight me'}
    </button>
  );
}

/** Design ↔ Dev Mode switch. */
function DevToggle() {
  const dev = useApp('ui', () => app.ui.devMode);
  const kind = useApp('doc', () => app.pageKind());
  if (kind === 'WHITEBOARD') return null;
  return (
    <button className={`btn ${dev ? 'dev-on' : 'secondary'}`} title="Dev Mode: inspect, measure and hand off (⇧D)" onClick={() => (app.setUI({ devMode: !dev }), app.setTool('select'))}>
      {'</>'} {dev ? 'Dev Mode' : 'Dev'}
    </button>
  );
}

function FileMenu() {
  const [open, setOpen] = useState(false);
  const item = (label: string, fn: () => void) => (
    <button
      onClick={() => {
        setOpen(false);
        fn();
      }}
    >
      <span>{label}</span>
    </button>
  );
  return (
    <div style={{ position: 'relative' }}>
      <button className="icon-btn" title="File menu" onClick={() => setOpen(!open)}>
        {Icons.chevronDown}
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 49 }} onPointerDown={() => setOpen(false)} />
          <div className="menu" style={{ position: 'absolute', left: 0, top: 32 }}>
            {item('All files…', () => app.setUI({ homeOpen: true }))}
            {item('Version history…', () => app.setUI({ versionsOpen: true }))}
            {!app.readOnly &&
              item('Create branch…', () => {
                const name = window.prompt('Branch name', 'Explorations')?.trim();
                if (name) void createBranch(name);
              })}
            {item('Import .fig / .json…', importFile)}
            {item('Figma access token…', () => app.setUI({ figmaTokenOpen: true }))}
            {item('Keyboard shortcuts', () => app.setUI({ shortcutsOpen: true }))}
            {item('Script console…', () => app.setUI({ scriptOpen: true }))}
            {item('Plugins…', () => app.setUI({ pluginsOpen: true }))}
            <hr />
            {item('Download file (.json)', () => {
              const blob = new Blob([JSON.stringify(app.doc.toJSON())], { type: 'application/json' });
              const a = document.createElement('a');
              a.href = URL.createObjectURL(blob);
              a.download = `${app.doc.get(app.doc.rootId)?.name ?? 'vellum'}.vellum.json`;
              a.click();
            })}
            {item('Add whiteboard page', () => app.editor.setPage(app.editor.addPage('Board', 'WHITEBOARD')))}
            {item('Add design page', () => app.editor.setPage(app.editor.addPage(undefined, 'DESIGN')))}
          </div>
        </>
      )}
    </div>
  );
}

export function importFile() {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.fig,.json,application/json';
  input.onchange = async () => {
    const f = input.files?.[0];
    if (!f) return;
    app.toast(`Importing ${f.name}…`);
    try {
      const r = await fetch(`/api/import?name=${encodeURIComponent(f.name.replace(/\.[^.]+$/, ''))}`, { method: 'POST', body: f });
      const res = (await r.json()) as { id?: string; error?: string };
      if (!r.ok || !res.id) throw new Error(res.error ?? 'Import failed');
      app.setUI({ homeOpen: false });
      app.connect(res.id);
    } catch (e) {
      app.toast(`Import failed: ${(e as Error).message}`);
    }
  };
  input.click();
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

const MENU: (string | '-')[] = ['copy', 'cut', 'duplicate', 'delete', '-', 'bringToFront', 'bringForward', 'sendBackward', 'sendToBack', '-', 'group', 'ungroup', 'frameSelection', 'section', 'autoLayout', '-', 'component', 'detach', 'mask', 'createStretchBrush', 'createScatterBrush', '-', 'union', 'subtract', 'intersect', 'exclude', 'flatten', '-', 'flipH', 'flipV', 'toggleVisible', 'toggleLock', '-', 'zoomSelection', 'selectAll'];

export function ContextMenu() {
  const m = useApp('ui', () => app.ui.contextMenu);
  if (!m) return null;
  const items = MENU.filter((id, i, arr) => id !== '-' || (i > 0 && arr[i - 1] !== '-'));
  const x = Math.min(m.x, window.innerWidth - 240);
  const y = Math.min(m.y, window.innerHeight - 560);
  return (
    <>
      <div style={{ position: 'fixed', inset: 0, zIndex: 49 }} onPointerDown={() => app.setUI({ contextMenu: null })} onContextMenu={(e) => (e.preventDefault(), app.setUI({ contextMenu: null }))} />
      <div className="menu" style={{ left: x, top: Math.max(8, y) }}>
        {items.map((id, i) => {
          if (id === '-') return <hr key={i} />;
          const c = command(id);
          const enabled = !c.enabled || c.enabled();
          return (
            <button
              key={id}
              disabled={!enabled}
              onClick={() => {
                app.setUI({ contextMenu: null });
                c.run();
              }}
            >
              <span>{c.label}</span>
              <span className="keys">{formatKeys(c.keys)}</span>
            </button>
          );
        })}
      </div>
    </>
  );
}

export function Toast() {
  const t = useApp('ui', () => app.ui.toast);
  return t ? <div className="toast">{t}</div> : null;
}

export function Fps() {
  const fps = useApp('ui', () => app.ui.fps);
  const show = new URLSearchParams(location.search).has('stats');
  if (!show) return null;
  const r = (window as unknown as { __vellumStats?: () => string }).__vellumStats?.() ?? '';
  return (
    <div className="fps">
      {fps} fps · {app.ui.renderer} {r}
    </div>
  );
}

export function Shortcuts() {
  const open = useApp('ui', () => app.ui.shortcutsOpen);
  if (!open) return null;
  const groups: [string, (c: (typeof COMMANDS)[number]) => boolean][] = [
    ['Tools', (c) => c.id.startsWith('tool.')],
    ['Edit', (c) => ['undo', 'redo', 'copy', 'cut', 'duplicate', 'delete', 'selectAll', 'selectChildren', 'selectParent', 'rename'].includes(c.id)],
    ['Arrange', (c) => /group|frame|section|autoLayout|component|detach|bring|send|align|distribute|flip|union|subtract|intersect|exclude|flatten|toggle/i.test(c.id) && !c.id.startsWith('tool.')],
    ['View', (c) => c.id.startsWith('zoom') || c.id === 'present' || c.id === 'toggleUI'],
  ];
  return (
    <div className="modal-backdrop" onPointerDown={(e) => e.target === e.currentTarget && app.setUI({ shortcutsOpen: false })}>
      <div className="modal" style={{ width: 'min(860px, calc(100vw - 32px))' }}>
        <header>
          <span>Keyboard shortcuts</span>
          <button className="icon-btn" onClick={() => app.setUI({ shortcutsOpen: false })}>
            {Icons.close}
          </button>
        </header>
        <div className="body" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
          {groups.map(([title, f]) => (
            <div key={title}>
              <h5 style={{ marginTop: 0 }}>{title}</h5>
              {COMMANDS.filter((c) => c.keys?.length && f(c)).map((c) => (
                <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                  <span>{c.label}</span>
                  <span className="chip">{formatKeys(c.keys)}</span>
                </div>
              ))}
            </div>
          ))}
          <div>
            <h5 style={{ marginTop: 0 }}>Canvas</h5>
            {[
              ['Pan', 'Space + drag / scroll'],
              ['Zoom', '⌘ + scroll / pinch'],
              ['Deep select', '⌘ + click'],
              ['Duplicate while dragging', '⌥ + drag'],
              ['Resize from center', '⌥ + drag handle'],
              ['Keep proportions', '⇧ + drag handle'],
              ['Nudge', 'Arrows (⇧ ×10)'],
              ['Edit text', 'Enter / double-click'],
            ].map(([a, b]) => (
              <div key={a} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                <span>{a}</span>
                <span className="chip">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- home

interface FileMeta {
  id: string;
  name: string;
  updatedAt: number;
  kind: 'DESIGN' | 'WHITEBOARD';
  projectId?: string | null;
  role?: string;
}
interface TeamInfo {
  id: string;
  name: string;
  role: 'admin' | 'editor' | 'viewer';
  people: { id: string; email: string; name: string; role: string }[];
}
interface ProjectInfo {
  id: string;
  name: string;
  teamId: string | null;
}
interface AuditEntry {
  ts: number;
  actor: { name: string; kind: string };
  action: string;
  fileId?: string;
  detail?: Record<string, unknown>;
}

type HomeView = { kind: 'recents' } | { kind: 'drafts' } | { kind: 'project'; id: string } | { kind: 'team'; id: string } | { kind: 'audit' };

const jpost = (url: string, body: unknown, method = 'POST') => fetch(url, { method, headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }).then(async (r) => ({ ok: r.ok, body: await r.json().catch(() => ({})) }));

export function Home() {
  const open = useApp('ui', () => app.ui.homeOpen);
  const [files, setFiles] = useState<FileMeta[]>([]);
  const [teams, setTeams] = useState<TeamInfo[]>([]);
  const [projects, setProjects] = useState<ProjectInfo[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [view, setView] = useState<HomeView>({ kind: 'recents' });
  const [tick, setTick] = useState(0);
  const [naming, setNaming] = useState<string | null>(null);
  const reload = () => setTick((t) => t + 1);
  useEffect(() => {
    if (!open) return;
    const get = <T,>(u: string, set: (v: T) => void, fallback: T) =>
      fetch(u)
        .then((r) => r.json())
        .then((v) => set(Array.isArray(v) ? (v as T) : fallback))
        .catch(() => set(fallback));
    get<FileMeta[]>('/api/files', setFiles, []);
    get<TeamInfo[]>('/api/teams', setTeams, []);
    get<ProjectInfo[]>('/api/projects', setProjects, []);
    if (view.kind === 'audit') get<AuditEntry[]>('/api/audit', setAudit, []);
  }, [open, tick, view.kind]);
  if (!open) return null;
  const projectId = view.kind === 'project' ? view.id : null;
  const create = async (kind: 'DESIGN' | 'WHITEBOARD') => {
    const r = await jpost('/api/files', { name: kind === 'WHITEBOARD' ? 'Untitled board' : 'Untitled design', kind, projectId });
    if (!r.ok) return app.toast(r.body.error ?? 'Could not create the file');
    openFile(r.body.id);
  };
  const openFile = (id: string) => {
    app.setUI({ homeOpen: false });
    app.connect(id);
  };
  // inline "name it" field in the sidebar for new projects and teams
  const nameField = (key: string, placeholder: string, onName: (name: string) => Promise<unknown>) =>
    naming === key ? (
      <input
        key={key}
        className="home-name"
        autoFocus
        placeholder={placeholder}
        onBlur={() => setNaming(null)}
        onKeyDown={async (e) => {
          e.stopPropagation();
          const v = (e.target as HTMLInputElement).value.trim();
          if (e.key === 'Escape') setNaming(null);
          if (e.key === 'Enter' && v) {
            setNaming(null);
            await onName(v);
            reload();
          }
        }}
      />
    ) : null;
  const shown = files.filter((f) => (view.kind === 'drafts' ? !f.projectId : view.kind === 'project' ? f.projectId === view.id : view.kind === 'team' ? projects.some((p) => p.teamId === view.id && p.id === f.projectId) : true));
  const team = view.kind === 'team' ? teams.find((t) => t.id === view.id) : view.kind === 'project' ? teams.find((t) => t.id === projects.find((p) => p.id === view.id)?.teamId) : undefined;
  const title = view.kind === 'recents' ? 'Recents' : view.kind === 'drafts' ? 'Drafts' : view.kind === 'audit' ? 'Audit log' : view.kind === 'team' ? (team?.name ?? 'Team') : (projects.find((p) => p.id === projectId)?.name ?? 'Project');
  const navBtn = (v: HomeView, label: React.ReactNode, key: string) => (
    <button key={key} className={`home-nav ${JSON.stringify(v) === JSON.stringify(view) ? 'on' : ''}`} onClick={() => setView(v)}>
      {label}
    </button>
  );
  return (
    <div className="home" onPointerDown={(e) => e.stopPropagation()}>
      <div className="home-layout">
        <nav className="home-side">
          <h1>Vellum</h1>
          {navBtn({ kind: 'recents' }, 'Recents', 'recents')}
          {navBtn({ kind: 'drafts' }, 'Drafts', 'drafts')}
          {projects
            .filter((p) => !p.teamId)
            .map((p) => navBtn({ kind: 'project', id: p.id }, <>📁 {p.name}</>, p.id))}
          {nameField('project', 'Project name', (name) => jpost('/api/projects', { name, teamId: null })) ?? (
            <button className="home-nav add" onClick={() => setNaming('project')}>
              + New project
            </button>
          )}
          {teams.map((t) => (
            <div key={t.id} className="home-team">
              {navBtn({ kind: 'team', id: t.id }, <b>{t.name}</b>, t.id)}
              {projects
                .filter((p) => p.teamId === t.id)
                .map((p) => navBtn({ kind: 'project', id: p.id }, <>📁 {p.name}</>, p.id))}
              {t.role !== 'viewer' &&
                (nameField(`project:${t.id}`, `Project in ${t.name}`, (name) => jpost('/api/projects', { name, teamId: t.id })) ?? (
                  <button className="home-nav add" onClick={() => setNaming(`project:${t.id}`)}>
                    + Project
                  </button>
                ))}
            </div>
          ))}
          {nameField('team', 'Team name', (name) => jpost('/api/teams', { name })) ?? (
            <button className="home-nav add" onClick={() => setNaming('team')}>
              + New team
            </button>
          )}
          <div style={{ flex: 1 }} />
          {navBtn({ kind: 'audit' }, 'Audit log', 'audit')}
        </nav>
        <div className="inner">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1>{title}</h1>
              <div className="sub">{view.kind === 'audit' ? 'Sign-ins, sharing, file changes and every edit agents make, for the files you own.' : 'Open-source design & whiteboard canvas, built for humans and AI agents.'}</div>
            </div>
            {app.fileId && (
              <button className="btn secondary" onClick={() => app.setUI({ homeOpen: false })}>
                Back to file
              </button>
            )}
          </div>
          {view.kind === 'audit' ? (
            <table className="audit">
              <tbody>
                {audit.map((e, i) => (
                  <tr key={i}>
                    <td>{new Date(e.ts).toLocaleString()}</td>
                    <td>
                      {e.actor.kind === 'agent' ? '✦ ' : ''}
                      {e.actor.name}
                    </td>
                    <td>{e.action}</td>
                    <td>{e.fileId ? (files.find((f) => f.id === e.fileId)?.name ?? e.fileId) : ''}</td>
                    <td style={{ color: 'var(--text-2)' }}>{e.detail ? JSON.stringify(e.detail).slice(0, 80) : ''}</td>
                  </tr>
                ))}
                {!audit.length && (
                  <tr>
                    <td>No events yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <>
              {view.kind === 'team' && team && <TeamPanel team={team} onChange={reload} />}
              {view.kind !== 'team' && (
                <div className="new">
                  <button onClick={() => create('DESIGN')}>
                    <i style={{ background: '#0d99ff' }}>{Icons.frame}</i> New design file
                  </button>
                  <button onClick={() => create('WHITEBOARD')}>
                    <i style={{ background: '#9747ff' }}>{Icons.sticky}</i> New whiteboard
                  </button>
                  <button onClick={importFile}>
                    <i style={{ background: '#1e1e1e' }}>↥</i> Import .fig or .json
                  </button>
                </div>
              )}
              <div className="grid">
                {shown.map((f) => (
                  <div key={f.id} className="card" onClick={() => openFile(f.id)}>
                    <div className="thumb">
                      <img loading="lazy" src={`/api/files/${f.id}/render?max=440&t=${Math.round(f.updatedAt)}`} alt="" />
                    </div>
                    <div className="meta">
                      <b>{f.name}</b>
                      <span>
                        {f.kind === 'WHITEBOARD' ? 'Whiteboard' : 'Design'} · edited {timeAgo(f.updatedAt)}
                        {f.role && f.role !== 'owner' ? ` · can ${f.role === 'editor' ? 'edit' : 'view'}` : ''}
                      </span>
                      {f.role !== 'viewer' && (
                        <select
                          className="share-select move"
                          title="Move to project"
                          value={f.projectId ?? ''}
                          onClick={(e) => e.stopPropagation()}
                          onChange={async (e) => {
                            const r = await jpost(`/api/files/${f.id}/move`, { projectId: e.target.value || null });
                            if (!r.ok) app.toast(r.body.error ?? 'Could not move the file');
                            reload();
                          }}
                        >
                          <option value="">Drafts</option>
                          {projects.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.teamId ? `${teams.find((t) => t.id === p.teamId)?.name ?? 'Team'} / ` : ''}
                              {p.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Team members: admins add people by email and set their role. */
function TeamPanel({ team, onChange }: { team: TeamInfo; onChange: () => void }) {
  const admin = team.role === 'admin';
  const set = async (email: string, role: string | null) => {
    const r = await jpost(`/api/teams/${team.id}/members`, { email, role });
    if (!r.ok) app.toast(r.body.error ?? 'Could not change the team');
    onChange();
  };
  return (
    <div className="team-panel">
      <h5>Members</h5>
      {team.people.map((p) => (
        <div className="share-person" key={p.id}>
          <span className="avatar" style={{ background: '#0d99ff' }}>{p.name.slice(0, 1).toUpperCase()}</span>
          <span style={{ flex: 1 }}>
            {p.name} <span style={{ color: 'var(--text-2)' }}>{p.email}</span>
          </span>
          {admin ? (
            <select className="share-select" value={p.role} onChange={(e) => void set(p.email, e.target.value === 'remove' ? null : e.target.value)}>
              <option value="admin">admin</option>
              <option value="editor">can edit</option>
              <option value="viewer">can view</option>
              <option value="remove">remove</option>
            </select>
          ) : (
            <span style={{ color: 'var(--text-2)' }}>{p.role}</span>
          )}
        </div>
      ))}
      {admin && app.account.authEnabled && (
        <form
          className="row"
          style={{ gap: 6, marginTop: 8 }}
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            void set(String(f.get('email')), String(f.get('role')));
            e.currentTarget.reset();
          }}
        >
          <div className="field">
            <input name="email" type="email" placeholder="Add someone by email" required style={{ border: 0, background: 'transparent', outline: 'none', width: '100%' }} onKeyDown={(e) => e.stopPropagation()} />
          </div>
          <select name="role" defaultValue="editor" className="share-select">
            <option value="editor">can edit</option>
            <option value="viewer">can view</option>
            <option value="admin">admin</option>
          </select>
          <button className="btn primary" type="submit">
            Add
          </button>
        </form>
      )}
    </div>
  );
}

function timeAgo(t: number) {
  const s = (Date.now() - t) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

// ---------------------------------------------------------------- connect AI

function Code({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <pre>
      {text}
      <button
        onClick={() => {
          void navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        }}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </pre>
  );
}

export function ConnectDialog() {
  const open = useApp('ui', () => app.ui.shareOpen);
  const [repo, setRepo] = useState('/path/to/vellum');
  const [token, setToken] = useState<string | null>(null);
  useEffect(() => {
    fetch('/api/health')
      .then((r) => r.json())
      .then((h: { repo?: string }) => h.repo && setRepo(h.repo))
      .catch(() => {});
  }, []);
  if (!open) return null;
  const mcp = `${repo}/packages/mcp/src/index.ts`;
  const url = `${location.origin}/api/mcp`;
  // with accounts on (a hosted server), agents connect over HTTP with a token; locally they can also run the stdio server
  const hosted = app.account.authEnabled;
  const tok = token ?? '<your token>';
  return (
    <div className="modal-backdrop" onPointerDown={(e) => e.target === e.currentTarget && app.setUI({ shareOpen: false })}>
      <div className="modal">
        <header>
          <span>Connect an AI agent</span>
          <button className="icon-btn" onClick={() => app.setUI({ shareOpen: false })}>
            {Icons.close}
          </button>
        </header>
        <div className="body">
          Agents connect over MCP and edit this file live, alongside you. They can read the layer tree, see what you have selected, build whole screens or boards in one call, and take screenshots to check their work. Each agent shows up with its own cursor color and keeps its own undo history.
          {hosted ? (
            <>
              <AgentTokens onCreated={setToken} />
              <h5>Claude Code</h5>
              <Code text={`claude mcp add --transport http vellum ${url} --header "Authorization: Bearer ${tok}"`} />
              <h5>Codex</h5>
              <Code text={`export VELLUM_TOKEN=${tok}\ncodex mcp add vellum --url ${url} --bearer-token-env-var VELLUM_TOKEN`} />
              <h5>Any MCP client (JSON config)</h5>
              <Code text={JSON.stringify({ mcpServers: { vellum: { type: 'http', url, headers: { Authorization: `Bearer ${tok}` } } } }, null, 2)} />
              <h5>Shell / scripts</h5>
              <Code text={`curl -s ${location.origin}/api/agent/status -H "Authorization: Bearer ${tok}" -H "content-type: application/json" -d '{"args":{}}'`} />
            </>
          ) : (
            <>
              <h5>Claude Code</h5>
              <Code text={`claude mcp add vellum -- npx tsx ${mcp}`} />
              <h5>Codex</h5>
              <Code text={`codex mcp add vellum -- npx tsx ${mcp}`} />
              <h5>Any MCP client (JSON config)</h5>
              <Code text={JSON.stringify({ mcpServers: { vellum: { command: 'npx', args: ['tsx', mcp] } } }, null, 2)} />
              <h5>Or over HTTP, without a local process</h5>
              <Code text={`claude mcp add --transport http vellum ${url}\ncodex mcp add vellum --url ${url}`} />
              <h5>Shell / scripts</h5>
              <Code text={`npx tsx ${repo}/packages/server/src/cli.ts status\nnpx tsx ${repo}/packages/server/src/cli.ts create_nodes '{"nodes":[{"type":"sticky","text":"Hello from the CLI"}]}'`} />
            </>
          )}
          <h5>Then ask, for example</h5>
          <ul>
            <li>“Design a pricing page with three tiers in the open Vellum file.”</li>
            <li>“Turn my selected stickies into a flowchart.”</li>
            <li>“Make every button on this page a component and swap in instances.”</li>
          </ul>
          <div style={{ color: 'var(--text-2)' }}>Keyboard shortcuts: {COMMANDS.filter((c) => c.id.startsWith('tool.')).map((c) => `${c.label} ${formatKeys(c.keys)}`).join(' · ')}</div>
        </div>
      </div>
    </div>
  );
}
